"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, TrendingUp, SunMedium, CloudRain, Briefcase } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Midweek Corporate vs. Weekend Staycation Cycles",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage high corporate occupancy from Hinjawadi, Kharadi, and Viman Nagar from Monday to Thursday, while capturing lucrative weekend staycation and leisure travelers from Mumbai and surrounding hubs without price erosion.",
  metricLabel: "Strategy focus",
  metricValue: "Dual-segment hurdle rates & dynamic minimum stay fences"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting & Rate Wars",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate movements across MakeMyTrip, Agoda, and Booking.com across key micro-markets (Baner, Koregaon Park, Chakan). Automated alerts safeguard your ADR while outyielding competitor flash sales.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA tracking & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Leakage Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Prevent corporate contracted rate leakages onto public OTAs and B2B aggregators. We enforce strict parity, negotiate clean wholesale distribution, and route frequent business travelers through direct high-margin channels.",
  metricLabel: "Strategy focus",
  metricValue: "Rate integrity audits & direct corporate booking funnels"
}];
const seasonalitySeasons = [{
  period: "Oct - Mar",
  name: "Peak Corporate & MICE Season",
  badge: "High Demand",
  iconName: "Briefcase",
  driver: "Automotive summits, IT conferences, corporate annual meetings, and heavy wedding/banquet calendar.",
  action: "Dynamic surge pricing, unyielding room category differentials, and non-refundable corporate guarantees."
}, {
  period: "Jul - Sep",
  name: "Monsoon Leisure & Weekend Surge",
  badge: "Selective Yielding",
  iconName: "CloudRain",
  driver: "Monsoon tourism influx from Mumbai/Nashik seeking scenic getaways, weekend leisure stays, and staycations.",
  action: "Targeted 2-night minimum stays for weekends, bundled F&B experiences, and weekday corporate volume packages."
}, {
  period: "Apr - Jun",
  name: "Summer Shoulder & Transition Window",
  badge: "Pace Optimization",
  iconName: "SunMedium",
  driver: "Moderate corporate travel offset by university admissions, regional training programs, and family transits.",
  action: "Early-bird value packaging, closed user group (CUG) loyalty incentives, and proactive group displacement analysis."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapepune" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Pune
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Pune combines high-velocity IT and industrial corporate demand with robust weekend staycation flows and large-scale MICE events. Sunil Singh crafts data-driven revenue systems calibrated precisely for business hotels, transit properties, and boutique retreats across the Pune metropolitan region.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-secondary" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-secondary" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-secondary" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality & Demand Dynamics Block */}
        <div className="mt-16 md:mt-20 p-6 sm:p-8 md:p-10 rounded-xl border border-border bg-card shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-border/70">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-mono font-medium uppercase tracking-wider text-secondary mb-1">
                <TrendingUp className="size-4" />
                <span>Demand Architecture</span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-foreground">
                Pune Seasonality & Booking Windows
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              A structured roadmap to maximize RevPAR across fluctuating business quarters, monsoon peaks, and transitional shoulder windows.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {seasonalitySeasons.map((season, i) => <div key={season.period} data-index={i} className="flex flex-col justify-between rounded-lg border border-border/80 bg-muted/20 p-5 space-y-4">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="text-xs font-mono font-bold text-secondary tracking-wide">
                      {season.period}
                    </span>
                    <Badge variant="outline" className="text-[11px] border-secondary/30 text-secondary-foreground">
                      {season.badge}
                    </Badge>
                  </div>
                  <h4 className="font-serif text-lg font-bold text-foreground mb-2 flex items-center gap-2">
                    {season.iconName === "Briefcase" && <Briefcase className="size-4 text-secondary" />}
                    {season.iconName === "CloudRain" && <CloudRain className="size-4 text-secondary" />}
                    {season.iconName === "SunMedium" && <SunMedium className="size-4 text-secondary" />}
                    <span>{season.name}</span>
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {season.driver}
                  </p>
                </div>
                <div className="pt-3 border-t border-border/50 text-xs">
                  <span className="font-semibold text-foreground block mb-1">Yield Strategy:</span>
                  <span className="text-muted-foreground">{season.action}</span>
                </div>
              </div>)}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Pune hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}